import React, { useEffect, useState } from 'react'
import { Box, IconButton, TextField, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { inputSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import { normaliseDuration } from 'Pages/DsRepricing/@services/durationRange'
import { dataColors, dsColorNew } from '@palette'

/**
 * Two numeric boxes, a dash and "days": a rental duration as a range.
 * Extracted from Price limits' `DurationScopePicker` (CU-869fhtp3a, D3)
 * when the Activity log became its second consumer — one control, one
 * look, two declared meanings:
 *
 * - `openEnd` (Price limits): an empty second box reads `∞` — a limit
 *   says "from here up".
 * - mirror (Activity, `openEnd={false}`): an empty second box shows the
 *   first value in grey and means "exactly N" — a filter says *which*,
 *   and the brief asks for one value or a range, never "7 and longer".
 *   Committing fills the empty side from the other, so the control never
 *   produces an open range (one can still arrive by URL).
 *
 * `commitOn`: `'change'` emits on every keystroke, as Price limits always
 * did (its save debounce needs it) and without swapping or refusing a
 * half-typed number; `'blur'` keeps a local draft and emits on blur or
 * Enter through `normaliseDuration` — a "1" on its way to "14" is not a
 * request. `min`/`max` bound the draft: outside them the box goes red,
 * nothing is emitted and the previous value stands. A `label` frames the
 * control as one 40 px white box with a caption, the shape of the filter
 * bar; without it the row is bare, as in a table cell.
 *
 * `value` is `{min, max} | null` and `onChange` receives the same shape
 * (other keys of `value`, like Price limits' `unit`, are the caller's).
 */
const BOX = { style: { width: 46, textAlign: 'center' } }

// In mirror mode "exactly N" is drawn as `N – [N in grey]`, whichever
// way it arrived (typed, or `duration=7` from the URL): the second box
// stays a ghost when it only repeats the first.
function toDraft(value, mirror = false) {
	const min = value?.min ?? '', max = value?.max ?? ''
	return { min, max: mirror && max !== '' && max === min ? '' : max }
}

const same = (a, b) => (a?.min ?? null) === (b?.min ?? null) && (a?.max ?? null) === (b?.max ?? null)

export default function DurationRangeField({
	value, onChange,
	openEnd = false,
	commitOn = 'change',
	min, max,
	label = '',
	sx = {},
}) {
	const isAny = !value || (value.min == null && value.max == null)
	const [draft, setDraft] = useState(() => toDraft(value, !openEnd))
	const [error, setError] = useState(null)
	// A value set from outside — a URL, "Clear filters", a swap on
	// commit — replaces the draft and clears any red.
	useEffect(() => { setDraft(toDraft(value, !openEnd)); setError(null) }, [value?.min, value?.max])

	const live = commitOn === 'change'
	const shown = live ? toDraft(value) : draft

	const emit = (next) => {
		if (next != null && next.min == null && next.max == null) next = null
		if (!same(next, value)) onChange(next)
	}

	// Price limits' path, verbatim: parse the box and emit at once.
	const typeLive = (side, raw) => {
		const n = raw === '' ? null : Number(raw)
		emit({ min: value?.min ?? null, max: value?.max ?? null, [side]: n })
	}

	const type = (side, raw) => {
		if (live) return typeLive(side, raw)
		setDraft(d => ({ ...d, [side]: raw }))
		if (error) setError(null)
	}

	const commit = () => {
		if (live) return
		const { value: v, error: e } = normaliseDuration(draft, { min, max })
		if (e) { setError(e); return }
		let next = v
		if (next && !openEnd) {
			// Mirror: the empty side takes the other — "exactly N".
			next = { min: next.min ?? next.max, max: next.max ?? next.min }
		}
		setDraft(toDraft(next, !openEnd))
		emit(next)
	}

	const onKeyDown = (e) => {
		if (e.key === 'Enter') { e.preventDefault(); e.target.blur() }
	}

	const clear = () => { setDraft(toDraft(null)); setError(null); emit(null) }

	const minPlaceholder = openEnd ? 'min' : 'any'
	const maxPlaceholder = openEnd
		? (isAny ? 'max' : '∞')
		: (shown.min !== '' ? String(shown.min) : '')
	const tone = isAny ? dsColorNew.textSubtle : dsColorNew.textMuted
	const framed = !!label
	// Framed, the two boxes lose their own outline: the frame is the field, as the other filters
	const fieldSx = framed
		? { '& .MuiOutlinedInput-root': { height: 38, backgroundColor: 'transparent', fontSize: 16 },
		    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
		    '& .MuiInputBase-input': { py: 0, px: 0.5 } }
		: inputSx
	const showClear = !isAny || error || (!live && (draft.min !== '' || draft.max !== ''))

	const row = (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
			<TextField type="number" size="small" placeholder={minPlaceholder}
			           value={shown.min}
			           error={error === 'min' || error === 'both'}
			           onChange={(e) => type('min', e.target.value)}
			           onBlur={commit} onKeyDown={onKeyDown}
			           inputProps={{ ...BOX, min, max }}
			           sx={fieldSx}/>
			<Typography sx={{ color: tone, fontSize: 13, px: 0.25, fontWeight: isAny ? 400 : 700 }}>–</Typography>
			<TextField type="number" size="small" placeholder={maxPlaceholder}
			           value={shown.max}
			           error={error === 'max' || error === 'both'}
			           onChange={(e) => type('max', e.target.value)}
			           onBlur={commit} onKeyDown={onKeyDown}
			           inputProps={{ ...BOX, min, max }}
			           sx={fieldSx}/>
			<Typography sx={{ color: tone, fontSize: 12, ml: 0.5 }}>
				days
			</Typography>
			{showClear && (
				<IconButton size="small" onClick={clear}
				            title="Any duration"
				            sx={{ color: dsColorNew.textMuted,
				                  '&:hover': { color: dsColorNew.dangerRed } }}>
					<DeleteIcon fontSize="small"/>
				</IconButton>
			)}
		</Box>
	)

	if (!framed) return row
	// The shape of an outlined field of the filter band: 40 px, the label sitting on the border
	return (
		<Box sx={{ position: 'relative', height: 40, px: 1, display: 'flex', alignItems: 'center', width: 1,
		           bgcolor: dsColorNew.filterInputBg, border: '1px solid', borderRadius: '6px',
		           borderColor: error ? dsColorNew.dangerSoft : dsColorNew.grayBorder,
		           '&:hover': { borderColor: error ? dsColorNew.dangerSoft : dataColors.gray300 },
		           '&:focus-within': { borderColor: error ? dsColorNew.dangerRed : dsColorNew.appBlue },
		           ...sx }}>
			<Typography component="span" sx={{ position: 'absolute', top: -9, left: 9, px: 0.5, lineHeight: '16px',
			                                   fontSize: 12, color: error ? dsColorNew.dangerRed : dsColorNew.textMuted,
			                                   backgroundColor: dsColorNew.white, pointerEvents: 'none' }}>
				{label}
			</Typography>
			{row}
		</Box>
	)
}
